import { useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, CheckCircle2, CloudOff, Link2Off } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { INTERVIEW_MODE_LABELS, type CandidateBookingView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { errorMessage } from '../../../lib/api-client';
import { formatDateTime, formatTime } from '../../../lib/format';
import { bookingApi, bookingKey, useCandidateBooking } from '../api/interviews-api';
import { byDay, dayHeading } from '../interview-ui';
import styles from '../components/Interviews.module.css';

/** D13: a candidate reads Dhaka time — the zone the company interviews in. */
const CANDIDATE_ZONE = 'Asia/Dhaka';

/**
 * The candidate's page from N-27 / N-29's link (F10.04, F10.06) — no account (D16).
 * Designed at 375px first (CLAUDE.md §5). Choosing a time books it at once; the same
 * link then shows the booking and "Confirm I'll attend", and refuses a second choice
 * politely (QA test 3).
 */
export function InterviewBookingPage({ token }: { readonly token: string }): ReactNode {
  const query = useCandidateBooking(token);

  if (token === '') return <Notice icon={Link2Off} title="This link doesn't work" body="Use the link in the latest email we sent you." />;
  if (query.isPending) {
    return (
      <Frame>
        <SkeletonLines count={6} />
      </Frame>
    );
  }
  if (query.error) {
    if (query.error.status === 404) return <Notice icon={Link2Off} title="This link doesn't work" body={query.error.message} />;
    return <Notice icon={CloudOff} title="We couldn't load your interview" body="Check your connection and try again." action={<Button variant="solid" onClick={() => void query.refetch()}>Try again</Button>} />;
  }
  return <Booking token={token} view={query.data} />;
}

function Booking({ token, view }: { readonly token: string; readonly view: CandidateBookingView }): ReactNode {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const intro = `${view.roundName} for ${view.jobTitle} at ${view.companyName} · ${view.durationMinutes} minutes · ${INTERVIEW_MODE_LABELS[view.mode].toLowerCase()}`;

  const act = async (label: string, call: () => Promise<CandidateBookingView>): Promise<void> => {
    setPending(label);
    setProblem(null);
    try {
      queryClient.setQueryData(bookingKey(token), await call());
    } catch (error) {
      setProblem(errorMessage(error));
      await queryClient.invalidateQueries({ queryKey: bookingKey(token) });
    } finally {
      setPending(null);
    }
  };

  if (view.state === 'booked' && view.booked) {
    const booked = view.booked;
    return (
      <Frame>
        <p className={styles.quiet}>{view.companyName}</p>
        <h1 className={styles.bookingTitle}>{booked.isConfirmed ? `See you then, ${view.candidateFirstName}` : `You're booked, ${view.candidateFirstName}`}</h1>
        <p className={styles.bookingLead}>{intro}</p>
        <dl className={styles.facts}>
          <dt>When</dt>
          <dd>{formatDateTime(booked.startsAt, { timeZone: CANDIDATE_ZONE })}</dd>
          <dt>Where</dt>
          <dd>
            {booked.meetingLink ? (
              <a href={booked.meetingLink} className={styles.link} target="_blank" rel="noreferrer">
                {booked.meetingLink}
              </a>
            ) : (
              (booked.place ?? 'Online — the link will follow by email')
            )}
          </dd>
        </dl>
        {problem && <p className={styles.warning}>{problem}</p>}
        {booked.isConfirmed ? (
          <p className={styles.notice}>
            <CheckCircle2 size={15} aria-hidden /> You have confirmed you will attend. If something changes, reply to our email.
          </p>
        ) : (
          <Button variant="solid" size="lg" icon={CalendarCheck} isPending={pending === 'confirm'} onClick={() => void act('confirm', () => bookingApi.confirm(token))}>
            Confirm I'll attend
          </Button>
        )}
        <p className={styles.quiet}>Need a different time? Reply to the email this link came in, and the recruitment team will move it for you.</p>
      </Frame>
    );
  }

  if (view.state === 'choose' && view.slots.length > 0) {
    const days = byDay(view.slots, (slot) => slot.startsAt, CANDIDATE_ZONE);
    return (
      <Frame>
        <p className={styles.quiet}>{view.companyName}</p>
        <h1 className={styles.bookingTitle}>Choose a time, {view.candidateFirstName}</h1>
        <p className={styles.bookingLead}>{intro}. Times are Dhaka time. Choosing one books it straight away.</p>
        {problem && <p className={styles.warning}>{problem}</p>}
        <div className={styles.days}>
          {days.map((group) => (
            <section key={group.day} aria-label={dayHeading(group.day)}>
              <h2 className={styles.dayHeading}>{dayHeading(group.day)}</h2>
              <div className={styles.choices}>
                {group.rows.map((slot) => (
                  <Button
                    key={slot.startsAt}
                    variant="outline"
                    size="lg"
                    isPending={pending === slot.startsAt}
                    disabled={pending !== null && pending !== slot.startsAt}
                    onClick={() => void act(slot.startsAt, () => bookingApi.book(token, slot.startsAt))}
                  >
                    {formatTime(slot.startsAt, { timeZone: CANDIDATE_ZONE })}
                  </Button>
                ))}
              </div>
            </section>
          ))}
        </div>
        <p className={styles.quiet}>None of these suit you? Reply to the email this link came in.</p>
      </Frame>
    );
  }

  return (
    <Notice
      icon={Link2Off}
      title={view.state === 'choose' ? 'Every time has been taken' : 'This link is no longer open'}
      body={view.closedReason ?? `The ${view.companyName} recruitment team will be in touch.`}
    />
  );
}

function Frame({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <main className={styles.booking}>
      <div className={styles.bookingCard}>{children}</div>
    </main>
  );
}

function Notice({ icon: Icon, title, body, action }: { readonly icon: typeof Link2Off; readonly title: string; readonly body: string; readonly action?: ReactNode }): ReactNode {
  return (
    <Frame>
      <Icon size={28} aria-hidden />
      <h1 className={styles.bookingTitle}>{title}</h1>
      <p className={styles.bookingLead}>{body}</p>
      {action}
    </Frame>
  );
}
